import {clearInput} from '../util/ui';
import {renderOrderNotice, requestCartCalculation} from '../payment/order';
import {store} from '../store';
import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {$qsAll, $qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {maybeFetch} from '../../../@shared/ts/maybe';
import {getErrorString} from '../../../@shared/ts/error';
import {getLocaleText} from '../util/translation';
import {eventClick} from '../util/dom';

export function initGiftCardInput() {
	if (!Feature.enabled(FeatureFlag.GIFTCARD_INPUT)) {
		return;
	}

	displayGiftCardFeature();

	handleGiftcardInputEvents();
}

function displayGiftCardFeature() {
	for (const $form of $qsAll('.gift-card-option')) {
		$form.classList.remove('hide');
	}

	$qs('#gift-card-section')?.classList.remove('hide');
}

async function applyGiftCard(giftCardNumber: string): Promise<void> {
	const ajaxURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'admin_ajax_url');
	const applyGiftcardNonce = Feature.dynamicMetadata<string>(FeatureFlag.GIFTCARD_INPUT, 'pw_gift_cards_apply_nonce');
	if (!ajaxURL || !applyGiftcardNonce) {
		renderOrderNotice(getLocaleText('Something went wrong. Please try again.'));
		return;
	}

	const formData = new FormData();
	formData.append('action', 'pw-gift-cards-redeem');
	formData.append('card_number', giftCardNumber);
	formData.append('security', applyGiftcardNonce);

	const {error, result} = await maybeFetch<{success: boolean; data?: {message: string}}>(ajaxURL, {
		method: 'POST',
		body: formData,
	});

	const defaultErrorMessage = getLocaleText('Something went wrong. Please try again.');
	if (error || !result) {
		const errorMessage = getErrorString(error) || defaultErrorMessage;
		renderOrderNotice(errorMessage);
		return;
	}

	if (!result.success) {
		renderOrderNotice(result.data?.message ? result.data.message : defaultErrorMessage);
		return;
	}

	if (result.data?.message) {
		renderOrderNotice(result.data.message);
	}

	hideGiftCardInput();
}

function handleGiftcardInputEvents() {
	// For submitting gift card input
	for (const $form of $qsAll<HTMLFormElement>('form.pw-wc-gift-card')) {
		$form.addEventListener('submit', async event => {
			event.preventDefault();

			if (!$form.checkValidity()) {
				$form.reportValidity();
				return;
			}

			showGiftCardLoadingState();

			const data = new FormData(event.target as HTMLFormElement);
			const giftCardNumber = (data.get('card_number') as string)?.trim() ?? '';

			await applyGiftCard(giftCardNumber);
			await requestCartCalculation();

			hideGiftCardLoadingState();
		});
	}

	// For opening gift card input
	for (const $giftCard of $qsAll('.gift-card-option')) {
		$giftCard.addEventListener('click', showGiftCardInput);
		$giftCard.addEventListener('keypress', showGiftCardInput);
	}
}

function showGiftCardInput(event: MouseEvent | KeyboardEvent) {
	if (!eventClick(event)) {
		return;
	}

	for (const $coupon of $qsAll('form.pw-wc-gift-card')) {
		$coupon.classList.remove('hide');
	}

	for (const $option of $qsAll('.gift-card-option')) {
		$option.classList.add('hide');
	}

	$qsAll('.pp-dropdown', ($dd: HTMLElement) => {
		$dd?.classList.add('shorten');
	});

	$qs('#pp-modal-content')?.addEventListener('mousedown', detectExitTap);
}

function hideGiftCardInput() {
	for (const $giftCard of $qsAll('form.pw-wc-gift-card')) {
		$giftCard.classList.add('hide');
	}

	for (const $option of $qsAll('.gift-card-option')) {
		$option.classList.remove('hide');
	}

	$qsAll('.pp-dropdown', ($dd: HTMLElement) => {
		$dd?.classList.remove('shorten');
	});

	$qs('#pp-modal-content')?.removeEventListener('mousedown', detectExitTap);
	clearInput('.wc-gift-card-input');
}

function detectExitTap(e: Event) {
	for (const $el of $qsAll('form.pw-wc-gift-card')) {
		if ($el.contains(e.target as HTMLElement)) {
			return;
		}
	}

	hideGiftCardInput();
}

function hideGiftCardLoadingState() {
	store.dispatch(stopModalLoading());

	for (const $spinner of $qsAll('.wc-coupon-spinner')) {
		$spinner.classList.add('hide');
	}

	for (const $border of $qsAll('.wc-gift-card-input')) {
		$border.classList.remove('remove-right-border');
	}

	for (const $applyButton of $qsAll<HTMLInputElement>('.gift-card-apply')) {
		$applyButton.disabled = false;
	}
}

function showGiftCardLoadingState() {
	store.dispatch(startModalLoading());

	for (const $spinner of $qsAll('.wc-coupon-spinner')) {
		$spinner.classList.remove('hide');
	}

	// Replace right border of gift-card-input with spinner
	for (const $border of $qsAll('.wc-gift-card-input')) {
		$border.classList.add('remove-right-border');
	}

	for (const $applyButton of $qsAll<HTMLInputElement>('.gift-card-apply')) {
		$applyButton.disabled = true;
	}
}
